import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import InstitutionalShowcasePage from '../../components/institutional/InstitutionalShowcasePage';
import DepartmentClassicPage from '../../components/institutional/DepartmentClassicPage';
import DirectorateClassicPage from '../../components/institutional/DirectorateClassicPage';
import CenterClassicPage from '../../components/institutional/CenterClassicPage';
import UnitClassicPage from '../../components/institutional/UnitClassicPage';
import { getLegacyDepartmentComponent } from '../../data/legacyDepartmentRegistry';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const TYPE_PATH = { department: 'departments', center: 'centers', directorate: 'directorates', unit: 'units' };

function mapLegacyDepartment(item) {
  return {
    type: 'department',
    shortName: item.name,
    title: item.name,
    parentGroup: item.school,
    heroImage: item.hero_image,
    subtitle: item.description,
    overviewTitle: 'Department Overview',
    overview: String(item.description || '').split(/\n{2,}/).filter(Boolean),
    vision: item.vision,
    mission: item.mission,
    leader: item.hod_name ? {
      sectionTitle: 'Message from the Head of Department',
      name: item.hod_name,
      title: 'Head of Department',
      photo: item.hod_photo,
      bio: 'The department leadership coordinates academic quality, practical training and student development.',
      progress: [
        { title: 'Academic leadership', icon: '🏅', description: 'Coordinates departmental academic quality and student development.' },
        { title: 'Programme coordination', icon: '📚', description: 'Supports programme delivery and administrative coordination.' },
      ],
    } : null,
    offeringsTitle: 'Programmes',
    offerings: (item.programmes || []).map((p) => ({ title: p.programme_name, description: p.entry_requirements || 'Programme details and requirements are available from the department.', meta: p.award_type || p.duration })),
    features: [
      { title: 'Practical learning', description: 'Students are trained with a focus on polytechnic practical skills and workplace readiness.' },
      { title: 'Academic support', description: 'The department provides guidance through HOD, lecturers and departmental office support.' },
    ],
    contact: {
      title: 'Department Contact',
      address: `${item.school || 'Federal Polytechnic Bida'}, Niger State, Nigeria`,
      email: item.email,
      phone: item.phone,
      hours: 'Monday - Friday, 8:00 AM - 4:00 PM',
    },
    mapPoints: [
      { title: 'Department Office', address: `${item.school || 'Federal Polytechnic Bida'}, Niger State, Nigeria` },
    ],
    quickStats: [{ value: (item.programmes || []).length || 'ND/HND', label: 'Programmes' }],
    quickInfo: [
      { label: 'School', value: item.school || 'Federal Polytechnic Bida' },
      { label: 'HOD', value: item.hod_name || 'Not specified' },
      { label: 'Status', value: item.status || 'active' },
    ],
  };
}

export default function InstitutionalDetail({ pageType = 'department' }) {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [legacyComponent, setLegacyComponent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    async function load() {
      try {
        setLoading(true);
        setError('');
        setData(null);
        setLegacyComponent(null);
        const LegacyDepartment = pageType === 'department' ? getLegacyDepartmentComponent(slug) : null;
        const typePath = TYPE_PATH[pageType] || 'departments';
        let res = await fetch(`${API_BASE}/api/institutional-pages/${typePath}/${slug}`);
        if (res.ok) {
          const page = await res.json();
          const overviewText = Array.isArray(page.overview) ? page.overview.join(' ') : String(page.overview || '');
          const hasRichCmsContent = overviewText.length > 120 || (page.offerings || []).length || (page.gallery || []).length || (page.staff || []).length;

          // Existing hand-built department pages have richer content than the first CMS placeholders.
          // Use the old rich content first, while new/admin-only departments still use the CMS template.
          if (LegacyDepartment && !hasRichCmsContent) {
            if (alive) setLegacyComponent(() => LegacyDepartment);
            return;
          }

          if (alive) setData(page);
          return;
        }

        if (pageType === 'department' && LegacyDepartment) {
          if (alive) setLegacyComponent(() => LegacyDepartment);
          return;
        }

        if (pageType === 'department') {
          res = await fetch(`${API_BASE}/api/departments/${slug}`);
          if (res.ok) {
            const legacy = await res.json();
            if (alive) setData(mapLegacyDepartment(legacy));
            return;
          }
        }
        if (alive) setError('Page not found.');
      } catch (err) {
        if (alive) setError('Unable to load this page.');
      } finally {
        if (alive) setLoading(false);
      }
    }
    load();
    return () => { alive = false; };
  }, [slug, pageType]);

  if (loading) {
    return <main className="min-h-screen bg-slate-50 p-8"><div className="mx-auto max-w-5xl rounded-3xl bg-white p-8 font-bold shadow-sm">Loading page...</div></main>;
  }

  if (legacyComponent) {
    const LegacyDepartmentPage = legacyComponent;
    return <LegacyDepartmentPage />;
  }

  if (error || !data) {
    return (
      <main className="min-h-screen bg-slate-50 p-8">
        <div className="mx-auto max-w-3xl rounded-3xl bg-white p-8 text-center shadow-sm">
          <h1 className="text-2xl font-black">Page not found</h1>
          <p className="mt-3 text-slate-600">{error || 'The page may be inactive or unpublished.'}</p>
          <Link to={`/${TYPE_PATH[pageType] || 'departments'}`} className="mt-6 inline-flex rounded-xl bg-emerald-700 px-5 py-3 font-black text-white">Back to listing</Link>
        </div>
      </main>
    );
  }

  if (pageType === 'department') {
    return <DepartmentClassicPage data={data} />;
  }

  if (pageType === 'directorate') {
    return <DirectorateClassicPage data={data} />;
  }

  if (pageType === 'center') {
    return <CenterClassicPage data={data} />;
  }

  if (pageType === 'unit') {
    return <UnitClassicPage data={data} />;
  }

  return <InstitutionalShowcasePage data={data} />;
}
